import React, { useState } from 'react';
import { X, Table as TableIcon, Download, ChevronLeft, ChevronRight, Code, Database } from 'lucide-react';

const ViewDataModal = ({ isOpen, onClose, data, title, sql }) => {
    const [currentPage, setCurrentPage] = useState(1);
    const [activeTab, setActiveTab] = useState('data'); // 'data' or 'sql'
    const rowsPerPage = 10;

    if (!isOpen || !data || !Array.isArray(data) || data.length === 0) return null;

    // Get columns from first row keys
    const columns = Object.keys(data[0]);
    const totalPages = Math.ceil(data.length / rowsPerPage);

    // Pagination logic
    const indexOfLastRow = currentPage * rowsPerPage;
    const indexOfFirstRow = indexOfLastRow - rowsPerPage;
    const currentRows = data.slice(indexOfFirstRow, indexOfLastRow);

    const handleDownload = () => {
        // Simple CSV download logic
        const headers = columns.join(',');
        const csvRows = data.map(row =>
            columns.map(col => {
                const cell = row[col] === null || row[col] === undefined ? '' : row[col];
                // Escape quotes
                const escaped = ('' + cell).replace(/"/g, '""');
                return `"${escaped}"`;
            }).join(',')
        );
        const csvContent = [headers, ...csvRows].join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.setAttribute('href', url);
        link.setAttribute('download', `${title || 'chart-data'}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <div className="style-modal-overlay" onClick={onClose} style={{ zIndex: 10000 }}>
            <div className="style-modal-content" style={{ maxWidth: '800px', width: '90%' }} onClick={e => e.stopPropagation()}>
                {/* Header */}
                <div className="style-modal-header" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '16px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                        <div className="header-title-group">
                            <div className="icon-box-sm">
                                <TableIcon size={20} />
                            </div>
                            <div className="header-text">
                                <h3>Data View</h3>
                                <p>{title || 'Chart Data'}</p>
                            </div>
                        </div>
                        <div style={{ display: 'flex', gap: '8px' }}>
                            <button className="btn-secondary" onClick={handleDownload} title="Download CSV" style={{ padding: '8px' }}>
                                <Download size={18} />
                            </button>
                            <button className="btn-close-modal" onClick={onClose}>
                                <X size={20} />
                            </button>
                        </div>
                    </div>

                    {/* Tabs if SQL is available */}
                    {sql && (
                        <div style={{ display: 'flex', gap: '4px', background: 'var(--bg-tertiary)', padding: '4px', borderRadius: '8px', border: '1px solid var(--glass-border)' }}>
                            <button
                                onClick={() => setActiveTab('data')}
                                style={{
                                    padding: '8px 16px',
                                    borderRadius: '6px',
                                    background: activeTab === 'data' ? 'var(--bg-secondary)' : 'transparent',
                                    color: activeTab === 'data' ? 'var(--accent-primary)' : 'var(--text-dim)',
                                    fontWeight: 600,
                                    fontSize: '0.85rem',
                                    display: 'flex', alignItems: 'center', gap: '6px',
                                    boxShadow: activeTab === 'data' ? '0 2px 4px rgba(0,0,0,0.05)' : 'none'
                                }}
                            >
                                <Database size={14} />
                                Data Preview
                            </button>
                            <button
                                onClick={() => setActiveTab('sql')}
                                style={{
                                    padding: '8px 16px',
                                    borderRadius: '6px',
                                    background: activeTab === 'sql' ? 'var(--bg-secondary)' : 'transparent',
                                    color: activeTab === 'sql' ? 'var(--accent-primary)' : 'var(--text-dim)',
                                    fontWeight: 600,
                                    fontSize: '0.85rem',
                                    display: 'flex', alignItems: 'center', gap: '6px',
                                    boxShadow: activeTab === 'sql' ? '0 2px 4px rgba(0,0,0,0.05)' : 'none'
                                }}
                            >
                                <Code size={14} />
                                SQL Query
                            </button>
                        </div>
                    )}
                </div>

                {/* Body */}
                <div className="style-modal-body" style={{ padding: '0', background: 'var(--bg-primary)' }}>
                    {activeTab === 'data' ? (
                        <div className="table-container" style={{ maxHeight: '500px', overflow: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                                <thead style={{ background: 'var(--bg-secondary)', position: 'sticky', top: 0 }}>
                                    <tr>
                                        {columns.map((col, idx) => (
                                            <th key={idx} style={{
                                                padding: '12px 16px',
                                                textAlign: 'left',
                                                borderBottom: '1px solid var(--glass-border)',
                                                color: 'var(--text-dim)',
                                                fontWeight: 600
                                            }}>
                                                {col}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {currentRows.map((row, rowIdx) => (
                                        <tr key={rowIdx} style={{ borderBottom: '1px solid var(--glass-border)' }}>
                                            {columns.map((col, colIdx) => (
                                                <td key={colIdx} style={{
                                                    padding: '12px 16px',
                                                    color: 'var(--text-main)'
                                                }}>
                                                    {row[col] !== null ? String(row[col]) : '-'}
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div style={{ padding: '24px', overflow: 'auto', maxHeight: '500px' }}>
                            <div style={{
                                background: '#1e293b',
                                padding: '20px',
                                borderRadius: '12px',
                                color: '#e2e8f0',
                                fontFamily: 'monospace',
                                fontSize: '0.9rem',
                                border: '1px solid var(--glass-border)',
                                lineHeight: '1.6',
                                whiteSpace: 'pre-wrap'
                            }}>
                                {sql}
                            </div>
                            <div style={{ marginTop: '16px', fontSize: '0.85rem', color: 'var(--text-dim)' }}>
                                This is the raw SQL query used to generate the dataset above.
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer with Pagination (Only show for data tab) */}
                {activeTab === 'data' && (
                    <div className="style-modal-footer" style={{ justifyContent: 'space-between', padding: '16px 24px' }}>
                        <div style={{ color: 'var(--text-dim)', fontSize: '0.9rem' }}>
                            Showing {indexOfFirstRow + 1}-{Math.min(indexOfLastRow, data.length)} of {data.length} rows
                        </div>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                            <button
                                className="btn-secondary"
                                disabled={currentPage === 1}
                                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                style={{ padding: '8px', opacity: currentPage === 1 ? 0.5 : 1 }}
                            >
                                <ChevronLeft size={16} />
                            </button>
                            <span style={{ color: 'var(--text-main)', fontWeight: 500 }}>
                                Page {currentPage} of {totalPages}
                            </span>
                            <button
                                className="btn-secondary"
                                disabled={currentPage === totalPages}
                                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                style={{ padding: '8px', opacity: currentPage === totalPages ? 0.5 : 1 }}
                            >
                                <ChevronRight size={16} />
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ViewDataModal;
